import React, { memo, useEffect, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { FaAngleDown, FaArrowRight, FaWhatsapp } from 'react-icons/fa';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { toast } from 'react-toastify';
import QRCode from 'qrcode';
import { parseISO, format } from 'date-fns';
import pt from 'date-fns/locale/pt';
import api from '../../services/api';
import { updateRequisicao } from '../../services/prontuario';
import { requisicoes as dbrequisicoes } from '../../helpers/documentos/requisicoes';
import { anamnese } from '../../helpers/prontuario/anamnese';
import { cid } from '../../helpers/prontuario/cid';
import {
  normalizeWhatsappPhone,
  enviarArquivo,
} from '../../services/pdfWhatsapp';
import { removeBeforeBase64, dataExtenso, openBase64Pdf } from '../../util/utils';
import generateRequisicaoPDF from './generateRequisicaoPDF';
import { signDocument, getVidaasStatus } from '../../services/vidaasV2';

const cidValueLabelSorted = cid
  .map(i => ({ value: i.value, label: `${i.label} - ${i.value}` }))
  .sort((a, b) => a.value.localeCompare(b.value));

const TIPOS_REQUISICAO = [
  { id: 'LaboratoriaisSimples', nome: 'Laboratoriais Simples' },
  { id: 'LaboratoriaisEspeciais', nome: 'Laboratoriais Especiais' },
  { id: 'LaboratoriaisCompleto', nome: 'Laboratoriais Completo' },
  { id: 'OutrosExames', nome: 'Outros Exames' },
];

const urlValidacaoRequisicao = (codRequisicao, codPaciente) =>
  `https://icm-marilia.com/requisicao/validacao?requisicao=${codRequisicao}&paciente=${codPaciente}`;

const gerarQrValidacao = (codRequisicao, codPaciente) =>
  QRCode.toDataURL(urlValidacaoRequisicao(codRequisicao, codPaciente), {
    type: 'image/jpeg',
    width: 120,
  });

function RequisicaoMedica({ show, atual, medico, funcionario, onHide }) {
  const [isLoadingRequisicoes, setIsLoadingRequisicoes] = useState(true);
  const [localLoading, setLocalLoading] = useState(false);
  const [requisicaoGravada, setRequisicaoGravada] = useState(false);
  const [assinaturaDigital, setAssinaturaDigital] = useState(false);
  const [signing, setSigning] = useState(false);
  const [requisicoes, setRequisicoes] = useState([]);
  const [requisicao, setRequisicao] = useState({
    codPaciente: atual && atual.CODPAC ? atual.CODPAC : '',
    codMedico: atual && atual.CODREALIZOU ? atual.CODREALIZOU : '',
    nomeMedico: atual && atual.REALIZOU ? atual.REALIZOU : '',
    nomePaciente: atual && atual.NOMEPAC ? atual.NOMEPAC : '',
    codConsulta: atual && atual.CODCONSULTA ? atual.CODCONSULTA : '',
    dataRequisicao: format(new Date(), 'dd/MM/yyyy'),
    tipo: '',
    descricao: '',
    cid: '',
    indicacao: '',
  });
  const codFunc = medico?.idFunc || funcionario?.codFunc;
  const isDoctorLoggedIn = !!medico;
  const isFuncionario = !!funcionario;

  const renderTreeExames = nodes => (
    <TreeItem key={nodes.id} itemId={nodes.id} label={nodes.name}>
      {Array.isArray(nodes.children) &&
        nodes.children.map(node => renderTreeExames(node))}
    </TreeItem>
  );

  const handleSelectExames = (event, nodeId) => {
    anamnese.complementares.children.forEach(classe => {
      classe.children.forEach(e => {
        if (e.id === nodeId) {
          setRequisicao({
            ...requisicao,
            descricao: (requisicao.descricao ?? '') + '\n        - ' + e.name,
          });
          event.stopPropagation();
        }
      });
    });
  };

  const handleVidaasAssinarReq = async content => {
    try {
      const vidaasStatus = await getVidaasStatus();
      if (!vidaasStatus?.authenticated) {
        toast.warn('Usuário ainda não autenticado no VIDAAS!');
        return;
      }

      const base64Strip = removeBeforeBase64(content);
      const payload = {
        documentType: 'requisicao',
        documentId: requisicao.codRequisicao.toString(),
        alias: requisicao.codRequisicao + '-requisicao.pdf',
        signatureFormat: 'CAdES_AD_RT',
        hashAlgorithm: '2.16.840.1.101.3.4.2.1',
        base64Content: base64Strip,
      };

      setSigning(true);
      const resp = await signDocument(payload);

      if (resp?.fileBase64Signed) {
        openBase64Pdf(resp.fileBase64Signed);
        return;
      }

      toast.warn('Assinatura não encontrada!');
    } catch (error) {
      toast.warn(
        error?.response?.data?.error || 'Usuário ainda não autenticado!'
      );
    } finally {
      setSigning(false);
    }
  };

  const criarRequisicao = payload => {
    api
      .post('requisicao/', payload)
      .then(response => {
        handleRequisicoes(requisicao.codPaciente);
        toast.success(response.data.message);
      })
      .catch(error => {
        toast.error(
          error?.response?.data?.error || 'Erro ao inserir requisição!'
        );
      });
  };

  const handlePrescreverNovaRequisicao = () => {
    criarRequisicao({
      codConsulta: requisicao.codConsulta,
      codMedico: medico ? medico?.codMed : atual?.CODREALIZOU,
      codPaciente: requisicao.codPaciente,
      tipo: '',
      descricao: '',
      cid: atual?.CID ?? '',
      indicacao: atual?.CID3 ?? '',
    });
  };

  const handleGravarRequisicao = async () => {
    if (!requisicao.codRequisicao) {
      return toast.warn('Selecione uma requisição de exame!');
    }

    try {
      setLocalLoading(true);
      const res = await updateRequisicao(requisicao);
      toast.success(res.data.message);
      setRequisicaoGravada(true);
    } catch (error) {
      const apiError = error?.response?.data?.error;
      const validationErrors = error?.response?.data?.details
        ?.map(detail => detail.message)
        .filter(Boolean)
        .join(', ');
      const errorMessage = [apiError, validationErrors]
        .filter(Boolean)
        .join(' ');
      toast.error(errorMessage || 'Erro ao gravar requisição!');
      setRequisicaoGravada(false);
    } finally {
      setLocalLoading(false);
    }
  };

  function handleVisualizarRequisicao(requisicao) {
    setRequisicao({
      codRequisicao: requisicao?.CODREQUISICAO,
      codPaciente: requisicao?.CODPAC,
      codMedico: requisicao?.CODMEDICO,
      nomeMedico: requisicao?.NOMEMED,
      crm: requisicao?.CRMMED,
      cpf: requisicao?.CPFMED,
      rqe: requisicao?.RQE,
      nomePaciente: requisicao?.NOMEPAC,
      codConsulta: requisicao?.CODCONSULTA,
      dataRequisicao: requisicao?.dataFormatada,
      tipo: requisicao?.TIPOREQUISICAO,
      descricao: requisicao?.DESCRICAO ?? '',
      cid: requisicao?.CID ?? '',
      cid3: requisicao?.INDICACAO ?? '',
      indicacao: requisicao?.INDICACAO ?? '',
    });
  }

  function handleRequisicoes(codPac) {
    // TODO: colocar erro local aqui, atestados e receitas. erro global é UX ruim.
    setIsLoadingRequisicoes(true);
    api
      .get(`paciente/${codPac}/requisicoes`)
      .then(result => {
        const lista = Array.isArray(result?.data) ? result.data : [];
        if (lista.length === 0) {
          setRequisicoes([]);
          return;
        }

        const data = lista.map(item => ({
          ...item,
          dataFormatada: format(parseISO(item?.DATAREQUISICAO), 'dd/MM/yyyy', {
            locale: pt,
          }),
        }));
        handleVisualizarRequisicao(data[0]);
        setRequisicoes(data);
      })
      .catch(err =>
        toast.error(
          err?.response?.data?.error || 'Erro ao carregar requisições!'
        )
      )
      .finally(() => setIsLoadingRequisicoes(false));
  }

  const handleRepetirRequisicao = () => {
    const dt = format(new Date(), "dd 'de' MMMM 'de' yyyy", { locale: pt });
    const linhas = (requisicao?.descricao ?? '').split('\n');

    for (let i = 0; i < linhas.length; i++) {
      if (linhas[i].includes('Marília')) {
        linhas[i] = `Marília, ${dt}.`;
        break;
      }
    }

    const payload = {
      codConsulta: requisicao.codConsulta,
      codMedico: medico ? medico?.codMed : requisicao.codMedico,
      codPaciente: requisicao.codPaciente,
      tipo: requisicao.tipo,
      descricao: linhas.join('\n'),
      cid: requisicao.cid,
      indicacao: requisicao.indicacao,
    };

    criarRequisicao(payload);
  };

  const handleImprimirRequisicao = async (requisicao, tipo) => {
    const qrcodeBase64 = await gerarQrValidacao(
      requisicao.codRequisicao,
      requisicao.codPaciente
    );
    await generateRequisicaoPDF(
      requisicao,
      tipo,
      1.0,
      qrcodeBase64,
      codFunc,
      assinaturaDigital,
      isDoctorLoggedIn,
      handleVidaasAssinarReq
    );
  };

  const enviarPorWpp = async () => {
    if (!requisicao) {
      toast.warn('Selecione uma requisição!');
      return;
    }

    try {
      const qrcodeBase64 = await gerarQrValidacao(
        requisicao.codRequisicao,
        requisicao.codPaciente
      );

      // Gera o PDF em base64
      const pdfGenerator = await generateRequisicaoPDF(
        {
          ...requisicao,
          texto: requisicao.descricao,
          nomePaciente: requisicao.nomePaciente,
          data: requisicao.dataRequisicao,
          nomeMedico: requisicao.nomeMedico,
          crmMedico: requisicao.crm,
        },
        'envioWpp',
        1.0,
        qrcodeBase64,
        codFunc,
        assinaturaDigital,
        isDoctorLoggedIn
      );

      const celular = normalizeWhatsappPhone(atual && atual.CELULARPAC);
      if (!celular) {
        toast.warn('Número de celular não encontrado!');
        return;
      }

      const data = await enviarArquivo({
        pdfGenerator,
        to: celular,
        paciente: {
          id: requisicao.codPaciente,
          nome: requisicao.nomePaciente,
          medico: requisicao.nomeMedico,
        },
        codLaudo: requisicao.codRequisicao,
        fileName: `Requisicao_${requisicao.codPaciente}.pdf`,
        caption: `Olá *${requisicao.nomePaciente}*, tudo bem? 😊\nSegue em anexo o seu *Pedido de Exames*.\nQualquer dúvida, nossa equipe estará à disposição pelo WhatsApp 14-3402-5252\nICM - Instituto do Coração de Marília`,
        officialConfig: {
          procedimento: 'requisicao',
          documentTitle: 'Pedido de Exames',
        },
      });
      toast.success(data.message);
    } catch (e) {
      // TODO: mandar pro sentry
      toast.error(
        e?.response?.data?.error || 'Erro ao enviar requisição via WhatsApp!'
      );
    }
  };

  const handleTipoRequisicao = tipoId => {
    const tipo = TIPOS_REQUISICAO.find(item => item.id === tipoId);
    if (!tipo) return;

    const descricao = dbrequisicoes[tipo.id]
      .replace('@MARILIA', `Marília, ${dataExtenso(new Date().toDateString())}`)
      .replace('@PAC', atual?.NOMEPAC)
      .replace('@CONV', atual?.SIGLACONV)
      .replace('@CID10', requisicao.cid ?? '')
      .replace('@INDICA', requisicao.indicacao ?? '');

    setRequisicao({
      ...requisicao,
      tipo: tipo.nome,
      descricao,
    });
  };

  const handleChangeDescricaoIndicacao = e => {
    const indicacao = e.target.value;
    const updated = (requisicao.descricao ?? '').split('\n').map(linha => {
      if (linha.includes('INDICAÇÃO')) return 'INDICAÇÃO: ' + indicacao;

      if (linha.includes('CID')) return 'CID: ' + requisicao.cid;

      return linha;
    });

    setRequisicao({
      ...requisicao,
      indicacao,
      descricao: updated.join('\n'),
    });
  };

  function handleChangeCID(option) {
    const codigo = option.label.split(' - ')[0];
    const indicacao = option.value;
    const updated = (requisicao.descricao ?? '').split('\n').map(linha => {
      if (linha.includes('INDICAÇÃO')) return 'INDICAÇÃO: ' + indicacao;
      if (linha.includes('CID')) return 'CID: ' + codigo;
      return linha;
    });

    setRequisicao({
      ...requisicao,
      cid: codigo,
      cid3: indicacao,
      indicacao,
      descricao: updated.join('\n'),
    });
  }

  useEffect(() => {
    const codPac = atual?.CODPAC;
    if (!codPac) {
      setIsLoadingRequisicoes(false);
      return;
    }
    handleRequisicoes(codPac);
  }, [atual?.CODPAC]);

  return (
    <Modal show={show} size="xl">
      <Modal.Header className="font-weight-bold">
        REQUISIÇÃO DE EXAMES
      </Modal.Header>
      <Modal.Body style={{ display: 'flex' }}>
        {isLoadingRequisicoes ? (
          <div
            className="d-flex justify-content-center align-items-center w-100 py-5"
            style={{ minHeight: 200 }}
          >
            <Spinner
              animation="border"
              role="status"
              aria-label="Carregando requisições"
            />
          </div>
        ) : (
          <>
            <div style={{ flex: '0 0 10.5%' }}>
              {requisicoes?.length > 0 ? (
                <ButtonGroup vertical>
                  {requisicoes.map(req => (
                    <Button
                      variant="outline-primary"
                      key={req.CODREQUISICAO}
                      onClick={() => {
                        handleVisualizarRequisicao(req);
                        setRequisicaoGravada(true); // Garante que requisições salvas permitam impressão
                      }}
                    >
                      {req.dataFormatada}
                    </Button>
                  ))}
                </ButtonGroup>
              ) : (
                <i className="text-danger">Paciente não tem requisição!</i>
              )}
            </div>

            <Card style={{ flex: 1, display: 'flex', padding: '0.5rem' }}>
              <Form noValidate>
                <Form.Row>
                  <Form.Group as={Col} className="font-weight-bold">
                    Paciente: {requisicao.nomePaciente}
                  </Form.Group>
                  <Form.Group as={Col} className="font-weight-bold">
                    Médico: {requisicao.nomeMedico}
                  </Form.Group>
                </Form.Row>
                <Button
                  onClick={() => {
                    handlePrescreverNovaRequisicao();
                    setRequisicaoGravada(false); // Reseta estado para nova requisição
                  }}
                >
                  Nova Requisição
                </Button>{' '}
                <Button
                  variant="secondary"
                  onClick={() => {
                    handleRepetirRequisicao();
                    setRequisicaoGravada(false); // Reseta estado para nova requisição repetida
                  }}
                >
                  Repetir Requisição
                </Button>{' '}
                <hr></hr>
                <Form.Row>
                  <Form.Group as={Col} sm={2} controlId="editCID">
                    <Form.Label className="m-0 font-weight-bold">CID</Form.Label>
                    <Form.Control
                      as="select"
                      name="cid"
                      aria-label="Selecione o CID"
                      value={requisicao?.cid3 || ''}
                      onChange={e => {
                        setRequisicaoGravada(false); // Se alterar algo, exige gravar novamente
                        const cidValue = cidValueLabelSorted.find(
                          opt => String(opt.value) === String(e.target.value)
                        );
                        if (cidValue) {
                          handleChangeCID(cidValue);
                        } else {
                          setRequisicao({ ...requisicao, cid: '', cid3: '' });
                        }
                      }}
                    >
                      <option value="" disabled>
                        {requisicao?.cid || 'Selecione o CID'}
                      </option>
                      {cidValueLabelSorted.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <Form.Group as={Col} sm={10}>
                    <Form.Label className="m-0 font-weight-bold">
                      INDICAÇÃO
                    </Form.Label>
                    <Form.Control
                      value={requisicao.indicacao || ''}
                      onChange={e => {
                        setRequisicaoGravada(false);
                        handleChangeDescricaoIndicacao(e);
                      }}
                      onBlur={handleChangeDescricaoIndicacao}
                    />
                  </Form.Group>
                </Form.Row>
                <Form.Label className="m-0 font-weight-bold">
                  Tipos de Requisição
                </Form.Label>
                <Row>
                  {TIPOS_REQUISICAO.map(tipo => (
                    <Col sm={3} key={tipo.id}>
                      <Form.Check
                        label={tipo.nome}
                        checked={requisicao.tipo === tipo.nome}
                        name="groupRequisicao"
                        type="radio"
                        id={tipo.id}
                        onChange={e => {
                          setRequisicaoGravada(false);
                          handleTipoRequisicao(e.target.id);
                        }}
                      />
                    </Col>
                  ))}
                </Row>
                <Form.Row style={{ marginTop: 5 }}>
                  <Col sm>
                    <Form.Group size="sm" controlId="editExamesComplementares">
                      <Form.Label className="font-weight-bold">
                        Descrição da Requisição - {requisicao.codRequisicao} -{' '}
                        {requisicao.dataRequisicao}
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        style={{ fontSize: '12px' }}
                        rows={
                          requisicao?.descricao
                            ? requisicao.descricao.split('\n').length
                            : 15
                        }
                        value={requisicao.descricao ?? ''}
                        onChange={e => {
                          setRequisicaoGravada(false);
                          setRequisicao({
                            ...requisicao,
                            descricao: e.target.value,
                          });
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <SimpleTreeView
                    aria-label="rich object"
                    defaultExpanded={['root']}
                    slots={{
                      expandIcon: FaArrowRight,
                      collapseIcon: FaAngleDown,
                    }}
                    getItemId={item => item.id}
                    onSelectedItemsChange={(e, id) => {
                      setRequisicaoGravada(false);
                      handleSelectExames(e, id);
                    }}
                  >
                    {renderTreeExames(anamnese.complementares)}
                  </SimpleTreeView>
                </Form.Row>
              </Form>
              <Form.Check
                type="switch"
                id="req-switch"
                defaultChecked={assinaturaDigital}
                label="Assinatura Digital"
                onChange={() => setAssinaturaDigital(!assinaturaDigital)}
              />
              <Card.Footer className="text-center">
                <Button onClick={handleGravarRequisicao} disabled={localLoading}>
                  {localLoading ? (
                    <Spinner animation="border" size="sm" role="status" />
                  ) : (
                    'Gravar'
                  )}
                </Button>{' '}
                <Button
                  variant="warning"
                  disabled={!requisicaoGravada}
                  onClick={() =>
                    handleImprimirRequisicao(requisicao, 'imprimirNormal')
                  }
                >
                  Imprimir
                </Button>{' '}
                <Button
                  variant="info"
                  disabled={!requisicaoGravada}
                  onClick={() =>
                    handleImprimirRequisicao(requisicao, 'imprimirTimbrado')
                  }
                >
                  Timbrado
                </Button>{' '}
                <Button
                  variant="outline-success"
                  disabled={isFuncionario || !requisicaoGravada}
                  onClick={enviarPorWpp}
                >
                  <FaWhatsapp /> Enviar PDF
                </Button>{' '}
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAssinaturaDigital(false);
                    onHide();
                  }}
                >
                  Fechar
                </Button>{' '}
                {signing && (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className=""> Assinando exame...</span>
                  </>
                )}
              </Card.Footer>
            </Card>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default memo(RequisicaoMedica);
